import { useState, type ReactNode } from 'react';

import { api } from '../api';
import { Aviso, Campo, Estado, Etiqueta, Painel, data, useAcao, useCarga } from '../comum';
import { CatalogoProibidos, FormularioDestino, resumirDestino } from './integracoes/partes';
import {
  ROTULO_TIPO,
  TIPOS_DE_EVENTO,
  type ConfigCatalogo,
  type DestinoPublico,
  type Entrega,
  type RespostaDestinos,
  type TipoDestino,
} from './integracoes/tipos';

/**
 * Integrações — a resposta a "o KACE não integra com nada".
 *
 * Três blocos, na ordem em que alguém os usa: para onde mandar (destinos), o
 * que observar (catálogo), e o que já foi mandado (trilha).
 */

export function Integracoes({
  aoPerderSessao,
  podeEditar,
}: {
  aoPerderSessao: () => void;
  podeEditar: boolean;
}): ReactNode {
  const acao = useAcao(aoPerderSessao);
  const [criando, setCriando] = useState(false);

  const destinos = useCarga<RespostaDestinos>(
    () => api.get('/api/integracoes/destinos'),
    [],
    aoPerderSessao,
  );
  const entregas = useCarga<{ itens: readonly Entrega[] }>(
    () => api.get('/api/integracoes/entregas'),
    [],
    aoPerderSessao,
  );
  const cfg = useCarga<ConfigCatalogo>(() => api.get('/api/config'), [], aoPerderSessao);

  const recarregarTudo = (): void => {
    destinos.recarregar();
    entregas.recarregar();
  };

  return (
    <>
      <div className="titulo-tela">
        <div>
          <h1>Integrações</h1>
          <p className="suave">
            O KACE enxerga o parque. Aqui o produto conta isso para os sistemas que você já usa — e
            só uma vez por fato.
          </p>
        </div>
        {podeEditar && !criando && (
          <button className="primario" onClick={() => setCriando(true)}>
            Novo destino
          </button>
        )}
      </div>

      {acao.erro !== null && <Aviso tipo="erro">{acao.erro}</Aviso>}
      {acao.ok !== null && <Aviso tipo="ok">{acao.ok}</Aviso>}

      {criando && (
        <FormularioDestino
          aoCancelar={() => setCriando(false)}
          aoSalvar={() => {
            setCriando(false);
            recarregarTudo();
          }}
          aoPerderSessao={aoPerderSessao}
        />
      )}

      <Painel titulo="Destinos">
        <Estado carga={destinos}>
          {destinos.dados !== null &&
            (destinos.dados.itens.length === 0 ? (
              <p className="suave">
                Nenhum destino. Sem um destino, os eventos do parque são detectados mas não vão a
                lugar nenhum.
              </p>
            ) : (
              <div className="rolagem">
                <table className="densa">
                  <thead>
                    <tr>
                      <th>Nome</th>
                      <th>Tipo</th>
                      <th>Onde</th>
                      <th>Estado</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {destinos.dados.itens.map((d) => (
                      <tr key={d.id}>
                        <td className="forte">{d.nome}</td>
                        <td>
                          <Etiqueta>{ROTULO_TIPO[d.tipo]}</Etiqueta>
                        </td>
                        <td className="suave mono" style={{ fontSize: 12 }}>
                          {resumirDestino(d)}
                        </td>
                        <td>
                          <Etiqueta tom={d.ativo ? 'ok' : 'neutro'}>
                            {d.ativo ? 'ativo' : 'desativado'}
                          </Etiqueta>
                        </td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          {podeEditar && (
                            <>
                              <button
                                disabled={acao.executando}
                                onClick={() =>
                                  void acao.rodar(async () => {
                                    const r = await api.post<{
                                      ok: boolean;
                                      detalhe: string;
                                    }>(`/api/integracoes/destinos/${d.id}/testar`);
                                    entregas.recarregar();
                                    if (!r.ok) throw new Error(r.detalhe);
                                    return r.detalhe;
                                  })
                                }
                              >
                                Testar
                              </button>{' '}
                              <button
                                disabled={acao.executando}
                                onClick={() =>
                                  void acao.rodar(async () => {
                                    await api.put(`/api/integracoes/destinos/${d.id}`, {
                                      ativo: !d.ativo,
                                    });
                                    destinos.recarregar();
                                    return d.ativo ? 'Destino desativado.' : 'Destino ativado.';
                                  })
                                }
                              >
                                {d.ativo ? 'Desativar' : 'Ativar'}
                              </button>{' '}
                              <button
                                className="perigo"
                                disabled={acao.executando}
                                onClick={() =>
                                  void acao.rodar(async () => {
                                    await api.del(`/api/integracoes/destinos/${d.id}`);
                                    recarregarTudo();
                                    // A trilha de entregas some junto: ela é
                                    // filha do destino no banco.
                                    return 'Destino excluído, com a trilha de entregas dele.';
                                  })
                                }
                              >
                                Excluir
                              </button>
                            </>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
        </Estado>

        <Aviso>
          <strong>O teste abre um chamado de verdade</strong> no sistema de destino — não há como
          testar criação de issue sem criar uma. O título começa com
          <span className="mono"> [TESTE]</span> e o corpo diz que pode ser fechada.
        </Aviso>
      </Painel>

      <CatalogoProibidos
        carga={cfg}
        podeEditar={podeEditar}
        acao={acao}
        aoVarrer={recarregarTudo}
      />

      <Painel
        titulo="Trilha de entregas"
        sub="Um envio por fato. Repetição não é reenviada — é assim que a varredura noturna não abre os mesmos chamados todo dia."
      >
        <Estado carga={entregas}>
          {entregas.dados !== null &&
            (entregas.dados.itens.length === 0 ? (
              <p className="suave">Nada entregue ainda.</p>
            ) : (
              <div className="rolagem">
                <table className="densa">
                  <thead>
                    <tr>
                      <th>Quando</th>
                      <th>Evento</th>
                      <th>Estado</th>
                      <th>Tentativas</th>
                      <th>Referência</th>
                    </tr>
                  </thead>
                  <tbody>
                    {entregas.dados.itens.map((e) => (
                      <tr key={e.id}>
                        <td className="suave">{data(e.criadaEm)}</td>
                        <td>
                          {e.payload.titulo ?? e.tipoEvento}
                          {e.erro !== null && <div className="erro-cartao">{e.erro}</div>}
                        </td>
                        <td>
                          <Etiqueta
                            tom={
                              e.estado === 'entregue'
                                ? 'ok'
                                : e.estado === 'falhou'
                                  ? 'erro'
                                  : 'alerta'
                            }
                          >
                            {e.estado}
                          </Etiqueta>
                        </td>
                        <td className={e.tentativas >= 5 ? 'atencao' : 'suave'}>
                          {e.tentativas}
                          {e.tentativas >= 5 && ' · desistiu'}
                        </td>
                        <td className="mono">{e.referenciaExterna ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
        </Estado>

        {podeEditar && (
          <div className="linha" style={{ marginTop: 14 }}>
            <button
              className="encolhe"
              disabled={acao.executando}
              onClick={() =>
                void acao.rodar(async () => {
                  const r = await api.post<{ tentadas: number; entregues: number }>(
                    '/api/integracoes/reprocessar',
                  );
                  entregas.recarregar();
                  return `${r.entregues} de ${r.tentadas} reprocessadas.`;
                })
              }
            >
              Reprocessar as que falharam
            </button>
            <span className="cresce suave" style={{ fontSize: 12.5 }}>
              O produto já reprocessa sozinho a cada 5 minutos, até 5 tentativas.
            </span>
          </div>
        )}
      </Painel>
    </>
  );
}
